import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { RealtimeClient } from '@/lib/realtime';
import {
  formatDateTime,
  formatDuration,
  formatMoney,
  formatPhone,
  formatService,
  titleCase,
} from '@/lib/format';
import {
  Badge,
  Button,
  Card,
  CallStateBadge,
  ErrorState,
  IntentBadge,
  Loading,
  PageHeader,
  ScoreBadge,
  cx,
} from '@/components/ui';

interface CallDetailResponse {
  call: Record<string, any>;
  session: Record<string, any> | null;
  transcript: Array<{
    seq: number;
    role: string;
    text: string;
    confidence: number | null;
    created_at: string;
  }>;
  state_timeline: Array<{
    from_state: string | null;
    to_state: string;
    reason: string | null;
    created_at: string;
  }>;
  qualification: Record<string, any> | null;
  transfers: Array<Record<string, any>>;
  cost: Record<string, any> | null;
}

/**
 * Fetch the audio through the API client, then hand the player a blob URL.
 *
 * An <audio src> is fetched by the browser directly, so it carries no
 * Authorization header — pointing it at the API endpoint would just 401. The
 * alternative, a signed public link, would mean a recording reachable without
 * a session; a blob is scoped to this tab and dies with it.
 */
function useRecording(callId: string | undefined, hasRecording: boolean) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!callId || !hasRecording) return;
    let revoked: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const blob = await api.getBlob(`/calls/${callId}/recording`);
        if (cancelled) return;
        revoked = URL.createObjectURL(blob);
        setSrc(revoked);
      } catch {
        // A missing recording is not an error worth a banner: the row can
        // outlive the audio by up to a day when retention has just expired.
        if (!cancelled) setSrc(null);
      }
    })();

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [callId, hasRecording]);

  return src;
}

export default function CallDetail() {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const transcriptEnd = useRef<HTMLDivElement>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['call', callId],
    queryFn: () => api.get<CallDetailResponse>(`/calls/${callId}`),
    enabled: Boolean(callId),
  });

  const live = data?.call?.ended_at === null;
  // Only fetched once the call actually has a recording, and only after
  // the detail has loaded, so a finished call does not request audio that
  // was never made.
  const recordingSrc = useRecording(callId, Boolean(data?.call?.recording_url));

  // Follow a live call in realtime; a finished call needs no socket.
  useEffect(() => {
    if (!live || !callId) return;
    const client = new RealtimeClient();
    client.connect();
    const unsubscribe = client.subscribe((event) => {
      if (event.call_id !== callId) return;
      queryClient.invalidateQueries({ queryKey: ['call', callId] });
    });
    return () => {
      unsubscribe();
      client.close();
    };
  }, [live, callId, queryClient]);

  useEffect(() => {
    if (live) transcriptEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.transcript.length, live]);

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const { call, session, transcript, state_timeline, qualification, transfers, cost } = data;

  return (
    <div className="w-full">
      <PageHeader
        title={formatPhone(call.caller_number)}
        description={`${formatDateTime(call.started_at)} · ${formatDuration(call.duration_sec)}`}
        actions={
          <>
            <CallStateBadge state={call.state} kind={call.bot_kind} />
            <Button variant="secondary" onClick={() => navigate('/calls')}>
              Back
            </Button>
          </>
        }
      />

      {/* The ownership chain the product requires on every call. */}
      <Card title="Ownership chain" className="mb-4">
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <Chain label="Client" value={call.client_name} />
          <Chain label="Bot group" value={call.bot_group_name} />
          <Chain label="Bot" value={call.bot_name} />
          <Chain label="Campaign" value={call.vicidial_campaign} />
          <Chain label="Vicidial user" value={call.vicidial_user} />
          <Chain label="Lead" value={call.vicidial_lead_id} />
          <Chain label="AI session" value={session?.session_key} mono />
          <Chain label="Human agent" value={call.meta?.human_agent} last />
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Above the transcript on purpose: when a recording exists it is
              usually what somebody came to this page for, and the transcript
              is what they read while it plays. */}
          {call.recording_url ? (
            <Card
              title="Recording"
              description="Streamed from this account only. The link is not shareable."
            >
              <audio
                controls
                preload="none"
                className="w-full"
                src={recordingSrc ?? undefined}
              >
                Your browser cannot play audio.
              </audio>
              <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
                Recordings are deleted on this account's retention schedule.
                Download it from the player's menu if you need to keep it.
              </p>
            </Card>
          ) : null}

          <Card
            title="Transcript"
            description={live ? 'Updating live' : `${transcript.length} turns`}
            padded={false}
          >
            {transcript.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-ink-500">
                Nothing spoken yet.
              </p>
            ) : (
              <div className="max-h-[55vh] space-y-2 overflow-y-auto p-4">
                {transcript.map((line) => (
                  <div
                    key={line.seq}
                    className={cx(
                      'flex',
                      line.role === 'bot' ? 'justify-start' : 'justify-end',
                    )}
                  >
                    <div
                      className={cx(
                        'max-w-[80%] rounded-lg px-3 py-2 text-sm',
                        line.role === 'bot'
                          ? 'bg-brand-50 text-ink-800 dark:bg-ink-800 dark:text-ink-100'
                          : 'bg-ink-100 text-ink-800 dark:bg-ink-700 dark:text-ink-50',
                      )}
                    >
                      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-60">
                        {line.role === 'bot' ? 'Glee AI Bot' : titleCase(line.role)}
                      </div>
                      {line.text}
                    </div>
                  </div>
                ))}
                <div ref={transcriptEnd} />
              </div>
            )}
          </Card>

          <Card title="State timeline" padded={false}>
            <ol className="divide-y divide-line text-sm">
              {state_timeline.map((event, index) => (
                <li key={index} className="flex items-center gap-3 px-4 py-2">
                  <span className="w-36 shrink-0 text-xs text-ink-500">
                    {formatDateTime(event.created_at)}
                  </span>
                  <CallStateBadge state={event.to_state} kind={call.bot_kind} />
                  {event.reason && (
                    <span className="truncate text-xs text-ink-500 dark:text-ink-400">
                      {event.reason}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Qualification">
            {qualification ? (
              <dl className="space-y-2.5 text-sm">
                <Row label="Qualified">
                  <Badge tone={qualification.qualified ? 'green' : 'gray'}>
                    {qualification.qualified ? 'Yes' : 'No'}
                  </Badge>
                </Row>
                <Row label="Lead score">
                  <ScoreBadge score={qualification.lead_score} />
                </Row>
                <Row label="Intent">
                  <IntentBadge intent={qualification.intent} />
                </Row>
                <Row label="Services">
                  <div className="flex flex-wrap justify-end gap-1">
                    {(qualification.services ?? []).length === 0 ? (
                      <span className="text-ink-400">—</span>
                    ) : (
                      (qualification.services as string[]).map((service) => (
                        <Badge key={service} tone="blue">
                          {formatService(service)}
                        </Badge>
                      ))
                    )}
                  </div>
                </Row>
                {(qualification.missing_fields ?? []).length > 0 && (
                  <Row label="Missing">
                    <span className="text-xs text-amber-600">
                      {(qualification.missing_fields as string[]).join(', ')}
                    </span>
                  </Row>
                )}
                {qualification.summary && (
                  <div className="border-t border-line pt-2.5">
                    <dt className="text-xs uppercase tracking-wide text-ink-500">Summary</dt>
                    <dd className="mt-1 text-sm text-ink-700 dark:text-ink-200">
                      {qualification.summary}
                    </dd>
                  </div>
                )}
                {qualification.answers && Object.keys(qualification.answers).length > 0 && (
                  <div className="border-t border-line pt-2.5">
                    <dt className="mb-1 text-xs uppercase tracking-wide text-ink-500">
                      Collected
                    </dt>
                    <dd>
                      <dl className="space-y-1">
                        {Object.entries(qualification.answers as Record<string, unknown>).map(
                          ([key, value]) => (
                            <div key={key} className="flex justify-between gap-3 text-xs">
                              <span className="text-ink-500">{titleCase(key)}</span>
                              <span className="text-right text-ink-800 dark:text-ink-100">
                                {String(value)}
                              </span>
                            </div>
                          ),
                        )}
                      </dl>
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-sm text-ink-500">Not qualified yet.</p>
            )}
          </Card>

          <Card title="Transfers">
            {transfers.length === 0 ? (
              <p className="text-sm text-ink-500">No transfer attempted.</p>
            ) : (
              <ol className="space-y-3">
                {transfers.map((transfer) => (
                  <li
                    key={transfer.id}
                    className="rounded-md bg-sunken p-2.5 text-sm"
                  >
                    <div className="flex items-center justify-between">
                      <Badge
                        tone={
                          transfer.result === 'CONNECTED'
                            ? 'green'
                            : transfer.result === 'PENDING'
                              ? 'amber'
                              : 'red'
                        }
                      >
                        {transfer.result}
                      </Badge>
                      <span className="text-xs text-ink-500">attempt {transfer.attempt}</span>
                    </div>
                    <p className="mt-1 text-xs text-ink-600 dark:text-ink-300">
                      {transfer.kind?.replace(/_/g, ' ')} → {transfer.destination}
                    </p>
                    {transfer.human_agent && (
                      <p className="text-xs text-ink-600 dark:text-ink-300">
                        Agent: {transfer.human_agent}
                      </p>
                    )}
                    {transfer.wait_sec !== null && (
                      <p className="text-xs text-ink-500">Waited {transfer.wait_sec}s</p>
                    )}
                    {transfer.failure_reason && (
                      <p className="text-xs text-red-600">{transfer.failure_reason}</p>
                    )}
                    {transfer.fallback_action && (
                      <p className="text-xs text-ink-500">
                        Fallback: {transfer.fallback_action.replace(/_/g, ' ')}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </Card>

          {cost && (
            <Card title="Cost">
              <dl className="space-y-2 text-sm">
                <Row label="Billable minutes">{cost.billable_minutes}</Row>
                <Row label="Charge">{formatMoney(cost.total_client)}</Row>
                {/* total_actual and gross_margin are stripped for clients unless
                    the tenant has expose_provider_costs enabled. */}
                {cost.total_actual !== undefined && (
                  <>
                    <Row label="Provider cost">{formatMoney(cost.total_actual)}</Row>
                    <Row label="Gross margin">{formatMoney(cost.gross_margin)}</Row>
                  </>
                )}
              </dl>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-xs uppercase tracking-wide text-ink-500">{label}</dt>
      <dd className="text-right text-ink-800 dark:text-ink-100">{children}</dd>
    </div>
  );
}

function Chain({
  label,
  value,
  mono,
  last,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  last?: boolean;
}) {
  return (
    <>
      <span className="inline-flex flex-col rounded-md bg-sunken px-2 py-1">
        <span className="text-[10px] uppercase tracking-wide text-ink-400">{label}</span>
        <span
          className={cx(
            'text-ink-800 dark:text-ink-100',
            mono && 'font-mono text-[11px]',
            !value && 'text-ink-400',
          )}
        >
          {value ?? '—'}
        </span>
      </span>
      {!last && <span className="text-ink-300">→</span>}
    </>
  );
}
